import React, { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { Redirect, Route, useHistory } from 'react-router-dom';
import StylesApi from './core/api/StylesApi';
import { Loader } from './core/components';
import { AppContext } from './AppContext';
import MainLayout from './core/layout/Main/MainLayout';
import { useAuthenticationContext } from './core/services/AuthenticationProvider';
import { ActionStatusType } from './core/types/ActionStatusType';
import { DictionaryType } from './core/types/DictionaryType';
import {
  DashboardRoute,
  CaseDetailsRoute,
  CommunicationAcceptanceRoute,
  UserProfileRoute,
  MessagesRoutes,
  CustomerTransactionDetailsRoute,
  HelpRoute,
  PaymentRoute,
  PaymentCallbackRoute,
  UpcomingPaymentsRoute,
  GeneralUserDataRoute,
} from './routes';
import TranslationsApi from './core/api/TranslationsApi';
import { UserLanguageService, TranslationService } from './core/services';
import LanguageService from './core/services/LanguageService';
import { toBackendCulture, mapBrowserLanguage } from './core/i18n/languages';
import ApiProvider from './core/api/ApiProvider';
import ApplicationSettingsApi from './core/api/ApplicationSettingsApi';
import GlobalConfig from './core/api/GlobalConfig';

export const AppRoute = () => {
  const authContext = useAuthenticationContext();
  const history = useHistory();
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const isHideMypageCases = useQuery<ActionStatusType<boolean>>(
    'isHideMypageCases',
    () => {
      return ApiProvider.get<ActionStatusType<boolean>>('UserProfileModule/HideMypageCases');
    },
    {
      enabled: !!authContext.isAuthenticated,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    }
  );

  const whitelabeling = useQuery<ActionStatusType<DictionaryType<DictionaryType<string>>>>(
    'styles',
    StylesApi.fetchSettings,
    {
      enabled: !!authContext.isAuthenticated,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    }
  );

  const appSettings = useQuery<ActionStatusType<DictionaryType<string>>>(
    'appSettings',
    ApplicationSettingsApi.getApplicationSettings,
    {
      enabled: !!authContext.isAuthenticated,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      onSuccess: (data) => {
        document.title = data.data.PortalHeaderTitle;
      },
    }
  );

  const userLanguage = useQuery('userLanguage', TranslationsApi.fetchUserLang, {
    enabled: !!authContext.isAuthenticated,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    onSuccess: ({ data }) => {
      const stored = LanguageService.getStored();
      if (stored) {
        const culture = toBackendCulture(stored) ?? data;
        UserLanguageService.setLanguageCode(culture);
        if (culture && culture !== data) {
          TranslationsApi.changeLanguage(culture).catch(() => { });
        }
      } else {
        const browser = toBackendCulture(mapBrowserLanguage(navigator.language));
        UserLanguageService.setLanguageCode(data || browser || 'en-us');
      }
    },
  });

  const translations = useQuery('translations', TranslationsApi.fetchTranslations, {
    enabled: !!authContext.isAuthenticated,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    onSuccess: (data) => {
      TranslationService.setTranslations(data.data);
    },
  });

  useEffect(() => {
    if (authContext && !(authContext.isAuthenticated === true) && authContext.signinRedirect) {
      const useLegacyLogin = GlobalConfig.val().UseLegacyLogin;
      if (useLegacyLogin) {
        // For legacy login, trigger the OIDC redirect directly
        authContext.signinRedirect();
      } else {
        // For new login flow, redirect to the login page
        history.push('/signin');
      }
    }
  }, []);

  const knownRedirectPaths = (path: string) => {
    if (path === undefined) {
      return false;
    }
    if (path.startsWith('/userprofile#profile-password')) {
      return true;
    }
    if (path.startsWith('/userprofile')) {
      return true;
    }
    if (path.startsWith('/communicationacceptance')) {
      return true;
    }
    return false;
  };

  useEffect(() => {
    if (!isLoading) {
      return;
    }

    if (authContext.user == null) {
      setIsLoading(true);
      return;
    }

    if (authContext.isAuthenticated == null) {
      setIsLoading(true);
      return;
    }

    const passwordChangeRequired =
      authContext.user.profile?.password_change_required?.toLocaleLowerCase() === 'true';

    if (passwordChangeRequired && isLoading) {
      if (!knownRedirectPaths(history.location.pathname.toLocaleLowerCase())) {
        history.push('UserProfile#profile-password');
        setIsLoading(false);
        return;
      }
    }

    setIsLoading(false);
  }, [isLoading]);

  return (
    <>
      {!(authContext.isAuthenticated === true) || !whitelabeling.isFetched ? (
        <Loader />
      ) : (
        <AppContext.Provider
          value={{
            appSettings: appSettings ? appSettings.data?.data : undefined,
            translations: translations ? translations.data?.data : undefined,
            styles: whitelabeling.data ? whitelabeling.data.data : {},
            defaultLanguage: userLanguage ? userLanguage.data?.data : undefined,
            isHideMypageCases: isHideMypageCases ? isHideMypageCases.data?.data : false,
          }}
        >
          {whitelabeling.isLoading || userLanguage.isLoading || isHideMypageCases.isLoading ? (
            <Loader />
          ) : (
            <MainLayout>
              <Route exact path="/" component={DashboardRoute} />
              <Route exact path="/Home" component={DashboardRoute} />

              <PermissionRoute
                path="/CaseDetails/:caseNumber"
                hasPermission={!isHideMypageCases.data?.data}
                component={CaseDetailsRoute}
              />

              <Route
                exact
                path="/CommunicationAcceptance"
                component={CommunicationAcceptanceRoute}
              />

              <PermissionRoute
                path="/TransactionDetails/:invoiceNo/:creditorNo/:accountNo/:debtorNo"
                hasPermission={!isHideMypageCases.data?.data}
                component={CustomerTransactionDetailsRoute}
              />

              <Route exact path="/UserProfile" component={UserProfileRoute} />

              <PermissionRoute
                path="/UpcomingPayments/Index"
                hasPermission={!isHideMypageCases.data?.data}
                component={UpcomingPaymentsRoute}
              />

              <PermissionRoute
                path="/Payment/Index"
                hasPermission={!isHideMypageCases.data?.data}
                component={PaymentRoute}
              />

              <PermissionRoute
                path="/Payment/NetsCallBack"
                hasPermission={!isHideMypageCases.data?.data}
                component={PaymentCallbackRoute}
              />

              <Route exact path="/Messages/:messageId?" component={MessagesRoutes} />

              <Route exact path="/GeneralUserData" component={GeneralUserDataRoute} />

              <Route exact path="/Help" component={HelpRoute} />
            </MainLayout>
          )}
        </AppContext.Provider>
      )}
    </>
  );
};

type PermissionRouteType = {
  path: string;
  hasPermission: boolean;
  component: any;
};

const PermissionRoute = (props: PermissionRouteType) => {
  return (
    <>
      <Route
        exact
        path={props.path}
        render={() => (props.hasPermission ? <props.component /> : <Redirect to="/" />)}
      />
    </>
  );
};
